import React from 'react';
import { ArcaPlan } from '../../constants/arcaPlans';
import { fmtNum } from '../../utils/formatArcaNumbers';
import toTitleCase from '../../utils/toTitleCase';
import { PlanReturnView, todayPct } from '../../utils/transferReturns';

interface NewPlanSummaryTableProps {
	resolvedPlan: ArcaPlan;
	/** Rentabilidad y comisión de gestión de comparsa; `null` = sin datos («—»). */
	planReturn: PlanReturnView | null;
	translate: (key: string) => string;
}

/**
 * Tabla de datos del plan elegido en una sola columna (plan nuevo, sin origen): rentabilidad
 * neta, comisiones y perfil de riesgo. A diferencia de `ComparisonTable`, no hay comparación,
 * así que los valores van en negro (sin verde/rojo) — no hay contra qué medirlos.
 */
const NewPlanSummaryTable: React.FC<NewPlanSummaryTableProps> = ({
	resolvedPlan,
	planReturn,
	translate
}) => {
	const pct = (n: number | null | undefined) => (n != null ? `${fmtNum(n, 2)}%` : '—');

	// Comisión mostrada = solo gestión, la de comparsa (catálogo).
	const rows: { label: string; value: string }[] = [
		{
			label: planReturn
				? (
						translate('individual.exploration.chosenPlan.netReturnAnnual') ||
						'Rentabilidad neta anualizada {years}a'
					).replace('{years}', String(planReturn.periodYears))
				: translate('individual.exploration.chosenPlan.netReturn') ||
					'Rentabilidad neta anualizada',
			value: planReturn ? todayPct(translate, planReturn, pct(planReturn.annualPct)) : pct(null)
		},
		{
			label: translate('individual.newPlanSummary.feeMgmt') || 'Comisión de gestión',
			value: pct(planReturn?.managementFeePct)
		}
	];

	// Perfil de riesgo: fila oculta temporalmente (el dato sigue en resolvedPlan.riskProfile).

	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="align-bottom">
						<th className="w-3/5" />
						<th className="pl-2 pb-2 text-right font-normal">
							<span className="block text-xs font-medium leading-tight line-clamp-2">
								{toTitleCase(resolvedPlan.name)}
							</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={i} className="border-t border-border/40">
							<td className="py-2.5 pr-3 text-muted-foreground">{row.label}</td>
							<td className="py-2.5 pl-2 text-right font-semibold tabular-nums text-foreground">
								{row.value}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default NewPlanSummaryTable;
